/**
 * Managed-theater chrome: mode banner, managed pills, lock affordances.
 * Same design tokens as ContactRate — no redesign, just permission theater.
 */

import React from 'react';
import { usePortal, type PortalMode } from '../portal/PortalContext.js';

export function DemoTopBar() {
  const { mode, setMode, caps, toast } = usePortal();

  return (
    <>
      <div
        className="sticky top-0 z-40 border-b"
        style={{
          background: 'linear-gradient(90deg, #0B1220 0%, #1a1040 45%, #0B1220 100%)',
          borderColor: 'rgba(109,74,234,0.35)',
        }}
      >
        <div className="mx-auto flex flex-wrap items-center gap-3 px-4 py-2.5 md:px-7" style={{ maxWidth: 1296 }}>
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <span
              className="text-[10px] font-bold uppercase tracking-[0.12em] px-2 py-0.5 rounded-full flex-shrink-0"
              style={{ background: 'rgba(66,133,244,0.2)', color: '#8AB4F8', border: '1px solid rgba(66,133,244,0.35)' }}
            >
              Demo
            </span>
            <div className="min-w-0">
              <p className="text-[12.5px] font-semibold text-white truncate">
                ContactRate managed theater
              </p>
              <p className="text-[11px] text-white/55 truncate">{caps.subtitle}</p>
            </div>
          </div>

          <ModeToggle mode={mode} onChange={setMode} />
        </div>
      </div>

      {toast && (
        <div className="fixed bottom-20 md:bottom-6 left-1/2 z-50 -translate-x-1/2 px-4 py-2.5 rounded-[12px] shadow-lg text-[12.5px] font-medium text-white"
          style={{ background: '#0D1729', border: '1px solid rgba(109,74,234,0.4)', maxWidth: '90vw' }}>
          {toast}
        </div>
      )}
    </>
  );
}

function ModeToggle({ mode, onChange }: { mode: PortalMode; onChange: (m: PortalMode) => void }) {
  const opts: { id: PortalMode; label: string; hint: string }[] = [
    { id: 'managed', label: 'Managed', hint: 'View-only factory' },
    { id: 'crm', label: 'CRM unlock', hint: 'Work leftovers' },
  ];
  return (
    <div
      className="flex rounded-[10px] p-0.5 flex-shrink-0"
      style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}
    >
      {opts.map((o) => {
        const active = mode === o.id;
        return (
          <button
            key={o.id}
            type="button"
            onClick={() => onChange(o.id)}
            className="px-3 py-1.5 rounded-[8px] text-left transition-colors"
            style={{
              background: active ? 'linear-gradient(135deg, #8F6BFF 0%, #4A63E8 100%)' : 'transparent',
            }}
            title={o.hint}
          >
            <span className={`block text-[11.5px] font-semibold ${active ? 'text-white' : 'text-white/70'}`}>
              {o.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/** Inline pill for managed sections */
export function ManagedPill({ children = 'Managed by ContactRate' }: { children?: string }) {
  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.06em] px-2 py-0.5 rounded-full"
      style={{
        background: 'rgba(109,74,234,0.1)',
        color: '#6D4AEA',
        border: '1px solid rgba(109,74,234,0.22)',
      }}
    >
      <span aria-hidden>✦</span>
      {children}
    </span>
  );
}

/** Soft lock ribbon over a control cluster */
export function ViewOnlyRibbon({
  title = 'View only',
  detail = 'ContactRate runs this for you. Full visibility — no client edits.',
}: {
  title?: string;
  detail?: string;
}) {
  const { mode } = usePortal();
  if (mode === 'crm' && title === 'View only') {
    // still show for factory-only areas via explicit props
  }
  return (
    <div
      className="flex items-start gap-2.5 px-3 py-2.5 rounded-[10px] mb-4"
      style={{
        background: 'rgba(109,74,234,0.06)',
        border: '1px solid rgba(109,74,234,0.16)',
      }}
    >
      <span className="text-violet-DEFAULT text-[13px] mt-0.5">🔒</span>
      <div className="min-w-0">
        <p className="text-[12px] font-semibold text-ink">{title}</p>
        <p className="text-[11.5px] text-muted mt-0.5 leading-snug">{detail}</p>
      </div>
    </div>
  );
}

export function CrmUpsellCard() {
  const { mode, setMode } = usePortal();
  if (mode === 'crm') {
    return (
      <div
        className="rounded-[12px] px-4 py-3 mb-5 flex items-center justify-between gap-3"
        style={{
          background: 'rgba(21,150,98,0.08)',
          border: '1px solid rgba(21,150,98,0.25)',
        }}
      >
        <div>
          <p className="text-[12.5px] font-semibold text-ink">Workspace CRM unlocked</p>
          <p className="text-[11.5px] text-muted mt-0.5">
            Pipeline drag + lead disposition are on. Brain, sequences, and TCPA stay managed.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setMode('managed')}
          className="text-[11.5px] font-semibold px-3 py-1.5 rounded-btn flex-shrink-0"
          style={{ background: '#fff', border: '1px solid #E5EAF2', color: '#0D1729' }}
        >
          Back to managed
        </button>
      </div>
    );
  }
  return (
    <div
      className="rounded-[12px] px-4 py-3 mb-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3"
      style={{
        background: 'linear-gradient(135deg, rgba(109,74,234,0.08) 0%, rgba(66,133,244,0.06) 100%)',
        border: '1px solid rgba(109,74,234,0.2)',
      }}
    >
      <div className="min-w-0">
        <p className="text-[12.5px] font-semibold text-ink">No CRM? Unlock Workspace CRM</p>
        <p className="text-[11.5px] text-muted mt-0.5">
          After Ava works leads 7–14 days, use pipeline & leads for your own follow-up — still no access to numbers or compliance.
        </p>
      </div>
      <button
        type="button"
        onClick={() => setMode('crm')}
        className="text-[11.5px] font-semibold text-white px-3.5 py-2 rounded-btn flex-shrink-0"
        style={{ background: 'linear-gradient(135deg, #8F6BFF 0%, #4A63E8 100%)' }}
      >
        Preview CRM unlock
      </button>
    </div>
  );
}

/** Disable pointer events + show managed cursor when locked */
export function LockLayer({
  locked,
  children,
  message = 'Managed by ContactRate',
}: {
  locked: boolean;
  children: React.ReactNode;
  message?: string;
}) {
  const { flash } = usePortal();
  if (!locked) return <>{children}</>;
  return (
    <div
      className="relative"
      onClickCapture={(e) => {
        const t = e.target as HTMLElement;
        if (t.closest('a, button, select, input, textarea, [data-allow-click]')) {
          // allow nav links inside
          if (t.closest('a[href]')) return;
          if (t.closest('[data-allow-click]')) return;
          e.preventDefault();
          e.stopPropagation();
          flash(message);
        }
      }}
    >
      {children}
      <div
        className="pointer-events-none absolute inset-0 rounded-[12px]"
        style={{ boxShadow: 'inset 0 0 0 1px rgba(109,74,234,0.08)' }}
        aria-hidden
      />
    </div>
  );
}
